import { useEffect, useState } from 'react';
import TextField, { TextFieldProps } from '../TextField/TextField';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
  }
}

export type GoogleAddressFieldProps = TextFieldProps<string> & {
  id?: string;
  variant?:
    | 'outlined'
    | 'standard'
    | 'plain'
    | 'background'
    | 'dropdown'
    | 'lined'
    | 'none'
    | 'border'
    | 'noHighlight'
    | 'outlineShadow';
  ISOCountry?: string[];
  isActive?: boolean;
  borderColor?: string;
  inputClass?: string;
  autoFocus?: boolean;
  onGoogleAddressSelect: (addressComponents: AddressComponents) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
};
export type GooglePlace = {
  address_components: {
    types: string[];
    long_name: string;
    short_name: string;
  }[];
};
export type GoogleAutoComplete = {
  setComponentRestrictions: (restrictions: { country?: string[] }) => void;
  setFields: (feilds: string[]) => void;
  addListener: (event: string, callback: () => void) => void;
  getPlace: () => GooglePlace;
};
export type AddressComponentsTranslate = {
  street: string;
  streetNumber: string;
  province: string;
  city: string;
  country: string;
  postalcode: string;
};
export type AddressComponents = {
  address: string;
  province: string;
  city: string;
  country: string;
  postalcode: string;
};

const GoogleAddressField = (props: GoogleAddressFieldProps): React.JSX.Element => {
  const { variant, ISOCountry, isActive, borderColor, onGoogleAddressSelect, onBlur, onFocus, id } = props;
  const [ref, setRef] = useState<React.RefObject<HTMLInputElement | null>>();
  const [triggerAddressSelect, setTriggerAddressSelect] = useState(false);
  const [addressComponentGoogle, setAddressComponentGoogle] = useState<AddressComponents>();
  const [isoTemp, setISOTemp] = useState<string[] | undefined>();
  const [googleAutoCompleteState, setGoogleAutoompleteState] = useState<GoogleAutoComplete>();
  useEffect(() => {
    if (!arraysAreEqual(ISOCountry || [], isoTemp || [])) {
      setISOTemp(ISOCountry);
    }
  }, [ISOCountry]);

  useEffect(() => {
    if (triggerAddressSelect && addressComponentGoogle) {
      onGoogleAddressSelect(addressComponentGoogle);
      setTriggerAddressSelect(false);
    }
  }, [triggerAddressSelect]);

  const loadScript = (url: string, callback: () => void) => {
    if (window.google && window.google.maps) {
      // Google Maps API is already loaded
      callback();
      return;
    }
    let hasScript = false;
    const scriptsInHead = document.head.getElementsByTagName('script');
    for (let i = 0; i < scriptsInHead.length; i += 1) {
      if (scriptsInHead[i].src === url) {
        // Script with the same URL is already present, call the callback
        hasScript = true;
        return;
      }
    }

    if (!hasScript) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  };
  const handleScriptLoad = (autoCompleteRef: HTMLInputElement) => {
    const autoComplete: GoogleAutoComplete = new window.google.maps.places.Autocomplete(autoCompleteRef, {
      types: ['address'],
    }) as GoogleAutoComplete;
    if (isoTemp && isoTemp.length > 0) {
      autoComplete.setComponentRestrictions({
        country: isoTemp,
      });
    }
    setGoogleAutoompleteState(autoComplete);
    autoComplete.setFields(['address_component']);
    autoComplete.addListener('place_changed', () => {
      const place = autoComplete.getPlace();
      const addressComponents: AddressComponentsTranslate = {
        street: '',
        streetNumber: '',
        province: '',
        city: '',
        country: '',
        postalcode: '',
      };
      place.address_components?.forEach((c) => {
        if (c.types.includes('route')) {
          addressComponents.street = c.long_name;
        } else if (c.types.includes('street_number')) {
          addressComponents.streetNumber = c.long_name;
        } else if (c.types.includes('administrative_area_level_1')) {
          addressComponents.province = c.short_name;
        } else if (
          c.types.includes('locality') ||
          c.types.includes('sublocality_level_1') ||
          c.types.includes('administrative_area_level_3')
        ) {
          addressComponents.city = c.long_name;
        } else if (c.types.includes('country')) {
          addressComponents.country = c.short_name;
        } else if (c.types.includes('postal_code') || c.types.includes('postal_code_prefix')) {
          addressComponents.postalcode = c.short_name;
        }
      });
      const address: AddressComponents = {
        address: buildAddress(addressComponents),
        province: addressComponents.province,
        city: addressComponents.city,
        country: addressComponents.country,
        postalcode: addressComponents.postalcode,
      };
      setAddressComponentGoogle(address);
      setTriggerAddressSelect(true);
    });
  };
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (window.google && window.google.maps) {
        // Google Maps API is loaded
        if (ref?.current) {
          handleScriptLoad(ref.current);
          clearInterval(intervalId); // Stop the interval once Google Maps is loaded
        }
      }
    }, 100);

    return () => {
      clearInterval(intervalId); // Clear the interval on component unmount
    };
  }, [ref]);
  useEffect(() => {
    if (isoTemp && isoTemp.length > 0 && googleAutoCompleteState) {
      googleAutoCompleteState.setComponentRestrictions({
        country: isoTemp,
      });
    }
  }, [isoTemp]);

  useEffect(() => {
    const el = ref?.current;

    if (el) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=AIzaSyCz0451-GVNV2410rpMXFdP54WYsAplaGg&libraries=places`,
        () => handleScriptLoad(el),
      );
    }
  }, [ref]);
  return (
    <TextField
      {...props}
      id={id}
      variant={variant}
      isActive={isActive}
      borderColor={borderColor}
      onRefChange={setRef}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
  function buildAddress(addressComponents: AddressComponentsTranslate): string {
    if (addressComponents.streetNumber && addressComponents.street) {
      return `${addressComponents.streetNumber} ${addressComponents.street}`;
    }
    if (addressComponents.streetNumber) {
      return addressComponents.streetNumber;
    }
    return addressComponents.street;
  }
  function arraysAreEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    for (let i = 0; i < arr1.length; i += 1) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }

    return true;
  }
};

export default GoogleAddressField;
